import * as React from "react";
import { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  CircularProgress,
  Typography,
  Alert,
  Select,
  Chip,
  OutlinedInput,
} from "@mui/material";
import { useQuery } from "react-query";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { UserOut, UserRole } from "../generated/api-client";
import {
  CrudDataGridPage,
  CrudConfig,
  CrudField,
} from "../components/CrudDataGrid";
import { GridRowModel, useGridApiContext } from "@mui/x-data-grid";
import PersonIcon from "@mui/icons-material/Person";
import toast from "react-hot-toast";
import { useAuth } from "../contexts/AuthContext";
import { apiClient } from "../services/apiClient";
import { axiosInstance } from "../services/apiClient";

interface TeamInfo {
  slug: string;
  name: string;
}

// Cella di modifica dei team: multi-select a chip. Gli admin vedono comunque tutto,
// quindi per loro la selezione è disabilitata.
const TeamsEditCell: React.FC<{
  id: any;
  field: string;
  value?: string[] | null;
  row: any;
  teams: TeamInfo[];
}> = ({ id, field, value, row, teams }) => {
  const apiRef = useGridApiContext();
  const selected = Array.isArray(value) ? value : [];
  const nameOf = (slug: string) => teams.find((t) => t.slug === slug)?.name || slug;
  return (
    <Select
      multiple
      fullWidth
      size="small"
      disabled={row?.role === UserRole.Admin}
      value={selected}
      onChange={(e) => {
        const v = e.target.value;
        apiRef.current.setEditCellValue({
          id,
          field,
          value: typeof v === "string" ? v.split(",") : v,
        });
      }}
      input={<OutlinedInput />}
      renderValue={(vals) => (
        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "nowrap", overflow: "hidden" }}>
          {(vals as string[]).map((slug) => (
            <Chip key={slug} size="small" label={nameOf(slug)} />
          ))}
        </Box>
      )}
    >
      {teams.map((t) => (
        <MenuItem key={t.slug} value={t.slug}>
          {t.name}
        </MenuItem>
      ))}
    </Select>
  );
};

export const UsersPage: React.FC = () => {
  const { inviteUser, user } = useAuth();
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<UserRole>(UserRole.BaseUser);
  const [inviteLoading, setInviteLoading] = useState(false);

  // Verifica se l'utente ha i permessi per vedere questa pagina
  const hasPermission = user?.role === "admin" || user?.role === "mantainer";
  const isAdmin = user?.role === "admin";

  const { data: teams = [] } = useQuery<TeamInfo[]>(
    "admin-teams",
    async () => (await axiosInstance.get("/admin/teams")).data,
    { enabled: hasPermission }
  );

  const getRoleLabel = (role?: UserRole) => {
    if (!role) return "N/A";
    const roleMap: { [key: string]: string } = {
      baseUser: "Utente Base",
      mantainer: "Mantainer",
      admin: "Amministratore",
    };
    return roleMap[role] || role;
  };

  if (!hasPermission) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Non hai i permessi per accedere a questa sezione. Solo gli
          amministratori e i mantainer possono gestire gli utenti.
        </Alert>
      </Box>
    );
  }

  const fields: CrudField[] = [
    {
      field: "firstname",
      headerName: "Nome",
      width: 200,
      editable: true,
    },
    {
      field: "lastname",
      headerName: "Cognome",
      width: 200,
      editable: true,
    },
    {
      field: "email",
      headerName: "Email",
      width: 250,
      editable: true,
    },
    {
      field: "role",
      headerName: "Ruolo",
      width: 150,
      editable: true,
      type: "singleSelect",
      valueOptions: [
        { value: UserRole.BaseUser, label: getRoleLabel(UserRole.BaseUser) },
        { value: UserRole.Mantainer, label: getRoleLabel(UserRole.Mantainer) },
        { value: UserRole.Admin, label: getRoleLabel(UserRole.Admin) },
      ],
    },
    {
      field: "teams",
      headerName: "Team",
      width: 280,
      editable: isAdmin,
      valueFormatter: (value: any) => {
        if (!Array.isArray(value) || value.length === 0) return "—";
        return value
          .map((slug: string) => teams.find((t) => t.slug === slug)?.name || slug)
          .join(", ");
      },
      renderEditCell: (params: any) => (
        <TeamsEditCell
          id={params.id}
          field={params.field}
          value={params.value}
          row={params.row}
          teams={teams}
        />
      ),
    },
    {
      field: "is_active",
      headerName: "Attivo",
      width: 100,
      editable: true,
      type: "boolean",
    },
    {
      field: "is_verified",
      headerName: "Verificato",
      width: 100,
      editable: false,
      type: "boolean",
    },
  ];

  const handleOpenInviteDialog = () => {
    setInviteDialogOpen(true);
  };

  const handleCloseInviteDialog = () => {
    setInviteDialogOpen(false);
    setInviteEmail("");
    setInviteRole(UserRole.BaseUser);
  };

  const handleInviteUser = async () => {
    if (!inviteEmail.trim()) {
      toast.error("Inserisci un'email");
      return;
    }

    setInviteLoading(true);
    try {
      await inviteUser(inviteEmail.trim(), inviteRole);
      toast.success(`Invito inviato a ${inviteEmail}`);
      handleCloseInviteDialog();
      // Ricarica la lista utenti
      window.location.reload();
    } catch (err: any) {
      const errorMsg =
        err?.response?.data?.detail || "Errore nell'invio dell'invito";
      toast.error(errorMsg);
    } finally {
      setInviteLoading(false);
    }
  };

  const config: CrudConfig<UserOut, any> = {
    queryKey: "users",
    fields,
    listFn: async () => {
      return await apiClient.list_all_users_admin_users_get();
    },
    createFn: async (data: any) => {
      // Non permettere creazione diretta, solo tramite invito
      throw new Error("La creazione utenti è disponibile solo tramite invito");
    },
    updateFn: async (id: string, data: any) => {
      // Usa l'endpoint PUT per aggiornare l'utente
      const updateData: any = {};
      if (data.firstname !== undefined) updateData.firstname = data.firstname;
      if (data.lastname !== undefined) updateData.lastname = data.lastname;
      if (data.email !== undefined) updateData.email = data.email;
      if (data.role !== undefined) updateData.role = data.role;
      if (data.is_active !== undefined) updateData.is_active = data.is_active;

      let response = await axiosInstance.put(
        `/admin/users/${id}`,
        updateData
      );

      // I team li modifica solo l'admin, con un endpoint dedicato
      if (isAdmin && Array.isArray(data.teams)) {
        response = await axiosInstance.put(`/admin/users/${id}/teams`, {
          teams: data.teams,
        });
      }

      // Converti la risposta in UserOut
      const userData = response.data;
      return new UserOut({
        id: userData.id,
        email: userData.email,
        firstname: userData.firstname,
        lastname: userData.lastname,
        role: userData.role,
        is_active: userData.is_active,
        is_verified: userData.is_verified,
        teams: userData.teams,
      });
    },
    deleteFn: async (id: string) => {
      await apiClient.delete_user_admin_users__user_id__delete(id);
    },
    getId: (row: UserOut) => row.id || "",
    getCreateDto: (row: GridRowModel) => {
      return {
        firstname: row.firstname || "",
        lastname: row.lastname || "",
        email: row.email || "",
        role: row.role,
        is_active: row.is_active,
        teams: row.teams,
      };
    },
  };

  return (
    <Box>
      <CrudDataGridPage
        title="Utenti"
        config={config}
        icon={<PersonIcon fontSize="large" />}
        actions={
          <Button onClick={handleOpenInviteDialog} variant="contained">
            <PersonAddIcon sx={{ mr: 1 }} />
            Invita Utente
          </Button>
        }
      />

      {/* Dialog per invitare utenti */}
      <Dialog
        open={inviteDialogOpen}
        onClose={handleCloseInviteDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Invita Nuovo Utente</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <TextField
              label="Email"
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="nuovo.utente@example.com"
              fullWidth
              required
            />
            <TextField
              select
              label="Ruolo"
              value={inviteRole}
              onChange={(e) => setInviteRole(e.target.value as UserRole)}
              fullWidth
              required
            >
              <MenuItem value={UserRole.BaseUser}>
                {getRoleLabel(UserRole.BaseUser)}
              </MenuItem>
              <MenuItem value={UserRole.Mantainer}>
                {getRoleLabel(UserRole.Mantainer)}
              </MenuItem>
              <MenuItem value={UserRole.Admin}>
                {getRoleLabel(UserRole.Admin)}
              </MenuItem>
            </TextField>
            <Typography variant="body2" color="text.secondary">
              L'utente riceverà un'email con un link per completare la
              registrazione.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Box sx={{ pb: 2, pr: 2 }}>
            <Button onClick={handleCloseInviteDialog} disabled={inviteLoading}>
              Annulla
            </Button>
            <Button
              onClick={handleInviteUser}
              variant="contained"
              disabled={inviteLoading || !inviteEmail.trim()}
              sx={{ ml: 2 }}
            >
              {inviteLoading ? <CircularProgress size={24} /> : "Invia Invito"}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
